'use client'

import React, { memo, useCallback } from 'react'
import Image from 'next/image'
import classNames from 'classnames'
import { useTranslation } from 'app/i18n/client'
import { getCardBrand } from 'utils/paymentFormat'

import CustomRadioButton from './CustomRadioButton'

interface CreditCardData {
  sequence: string
  number: string
  name: string
  expire: string
  gateway: string
  brand: string
}

interface CreditCardProps {
  creditCardData?: CreditCardData
  isInactive?: boolean
  containerClassName?: string
  hasDeleteButton?: boolean
  isSelected?: boolean
  displayRadio?: boolean
  onDelete?: (creditCardData: CreditCardData) => void
  onClick?: (creditCardData?: CreditCardData) => void
}

const brandLogos: Record<string, string> = {
  visa: '/images/cards/visa.png',
  mastercard: '/images/cards/master-card.png',
  jcb: '/images/cards/jcb.png',
  amex: '/images/cards/ac-card.png',
  dinersclub: '/images/cards/dinner-club.png',
  diners: '/images/cards/dinner-club.png', // Support both 'dinersclub' and 'diners'
}

// Display card number exactly as returned from Tripla
const displayCardNumber = (num?: string) => {
  return num || '************'
}

// Shared sub-components
const CardBrandInfo = ({
  cardBrand,
  className = '',
}: {
  cardBrand?: string
  className?: string
}) => (
  <>
    {cardBrand && brandLogos[cardBrand] && (
      <div
        className={classNames(
          'flex-shrink-0',
          className === 'mobile' ? '' : 'mr-2',
        )}
      >
        <Image
          src={brandLogos[cardBrand]}
          alt={cardBrand}
          width={21}
          height={16}
        />
      </div>
    )}
    {cardBrand && (
      <span
        className={classNames(
          'text-[#111111] uppercase',
          className === 'mobile' ? 'text-xs' : 'text-sm mr-4',
        )}
      >
        {cardBrand}
      </span>
    )}
  </>
)

const CardNumber = ({
  number,
  className = '',
}: {
  number?: string
  className?: string
}) => (
  <span
    className={classNames(
      'text-[#111111]',
      className === 'mobile' ? 'text-xs ml-1' : 'text-sm',
    )}
  >
    {displayCardNumber(number)}
  </span>
)

const ExpiryInfo = ({
  expire,
  hasDeleteButton,
  t,
  className = '',
}: {
  expire?: string
  hasDeleteButton: boolean
  t: (key: string) => string
  className?: string
}) => (
  <div
    className={classNames(
      'flex items-center gap-2',
      className === 'mobile' ? '' : hasDeleteButton ? 'ml-4' : 'ml-auto',
    )}
  >
    <span
      className={classNames(
        'text-[#111111]',
        className === 'mobile' ? 'text-xs' : 'text-sm mr-2',
      )}
    >
      {t('Card Expiry')}
    </span>
    <span
      className={classNames(
        'text-[#111111]',
        className === 'mobile' ? 'text-xs' : 'text-sm',
      )}
    >
      {expire || ' '}
    </span>
  </div>
)

const CardHolderName = ({
  name,
  className = '',
}: {
  name?: string
  className?: string
}) => (
  <div
    className={classNames(
      'text-[#111111]',
      className === 'mobile' ? 'text-xs' : 'text-sm mt-2',
    )}
  >
    {name?.toUpperCase() || '-'}
  </div>
)

const DeleteButton = ({
  onDelete,
  t,
  className = '',
}: {
  onDelete: (e: React.MouseEvent) => void
  t: (key: string) => string
  className?: string
}) => (
  <button
    onClick={onDelete}
    className={classNames(
      'flex-[0_1_75px] bg-[#E7EFF8] text-[#336493] hover:bg-[#D9E7F5] hover:underline uppercase tracking-wider',
      className === 'mobile'
        ? 'text-xs py-3.5 px-4 max-[320px]:py-1.5 max-[320px]:px-3 h-[50px]'
        : 'flex-initial text-sm p-3',
    )}
    type="button"
  >
    {t('Delete')}
  </button>
)

const CreditCard: React.FC<CreditCardProps> = memo(
  ({
    creditCardData,
    isInactive = false,
    containerClassName,
    hasDeleteButton = false,
    isSelected = false,
    displayRadio = true,
    onDelete,
    onClick,
  }) => {
    const { t } = useTranslation()
    let cardBrand = creditCardData?.brand?.toLowerCase()

    if (creditCardData?.number) {
      const firstSixDigits = creditCardData?.number.slice(0, 6)
      const checkedCardBrand = getCardBrand(firstSixDigits)
      if (!cardBrand && checkedCardBrand) {
        cardBrand = checkedCardBrand
      }
    }

    // Only memoize event handlers to prevent child re-renders
    const handleDelete = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault()
        if (creditCardData) {
          onDelete?.(creditCardData)
        }
      },
      [creditCardData, onDelete],
    )

    // Empty Card
    if (!creditCardData) {
      return (
        <div
          className={classNames(
            'flex items-center w-full p-4 sm:p-3 h-16 bg-[#FAFAFA] border border-[#DFDFDF] cursor-pointer',
            'space-x-2',
            containerClassName,
          )}
          onClick={() => onClick?.(undefined)}
        >
          {/* Radio button for selection */}
          {displayRadio && (
            <CustomRadioButton
              name="new-card"
              isSelected={isSelected}
              isInactive={isInactive}
            />
          )}

          <span
            className={classNames(
              'text-sm text-[#111111]',
              !displayRadio && 'ml-2',
            )}
          >
            {t('Fill in a new card')}
          </span>
        </div>
      )
    }

    // Handle filled state - card is registered
    return (
      <div
        className={classNames(
          'flex items-center w-full p-4 sm:p-3 bg-[#FAFAFA] border border-[#DFDFDF]',
          'space-x-2',
          !isInactive && 'cursor-pointer',
          isInactive && 'opacity-50',
          containerClassName,
        )}
        onClick={() => (isInactive ? undefined : onClick?.(creditCardData))}
      >
        {/* Radio button for selection */}
        {displayRadio && (
          <CustomRadioButton
            name={creditCardData?.sequence}
            isSelected={isSelected}
            isInactive={isInactive}
          />
        )}

        {/* Card Content */}
        <div className="flex-1">
          {/* Mobile Layout (< sm) - Vertical Stack with right-aligned delete button */}
          <div className="sm:hidden flex justify-between items-center">
            <div className="flex-[0_1_75%] mr-4">
              <div className="flex flex-col gap-2.5">
                {/* First Row: Brand logo, brand name, card number */}
                <div className="flex flex-wrap items-center gap-2">
                  <CardBrandInfo cardBrand={cardBrand} className="mobile" />
                  <CardNumber
                    number={creditCardData?.number}
                    className="mobile"
                  />
                </div>

                {/* Second Row: Expiry date */}
                <ExpiryInfo
                  expire={creditCardData?.expire}
                  hasDeleteButton={hasDeleteButton}
                  t={t}
                  className="mobile"
                />

                {/* Third Row: Card holder name */}
                <CardHolderName
                  name={creditCardData?.name}
                  className="mobile"
                />
              </div>
            </div>

            {/* Delete button for mobile - stays on right */}
            {hasDeleteButton && (
              <DeleteButton onDelete={handleDelete} t={t} className="mobile" />
            )}
          </div>

          {/* Desktop Layout (>= sm) - Original horizontal layout */}
          <div className="hidden sm:block">
            <div className="flex justify-between items-center w-full">
              <div
                className={classNames(
                  'flex flex-col',
                  hasDeleteButton ? 'flex-[0_1_80%]' : 'flex-1',
                )}
              >
                <div className="flex items-center">
                  <CardBrandInfo cardBrand={cardBrand} />
                  <CardNumber number={creditCardData?.number} />
                  <ExpiryInfo
                    expire={creditCardData?.expire}
                    hasDeleteButton={hasDeleteButton}
                    t={t}
                  />
                </div>
                <CardHolderName name={creditCardData?.name} />
              </div>

              {/* Delete button for desktop */}
              {hasDeleteButton && (
                <DeleteButton onDelete={handleDelete} t={t} />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  },
)

CreditCard.displayName = 'CreditCard'

export default CreditCard
